/* ==========================================================================
   PrimeTech   front-end entry point.

   Architecture:
     - motion/scroll.js   : Lenis smooth scroll + GSAP ScrollTrigger glue
     - motion/reveal.js   : generic reveal-on-scroll for .reveal elements
     - motion/marquee.js  : infinite brand strip animation
     - scenes/hero.js     : Three.js hero scene (orb + particles)
     - ui/nav.js          : sticky nav + mobile drawer behavior

   Heavy modules (Three.js, R3F) are dynamically imported so mobile users
   don't pay the cost unless a hero canvas is actually on the page.
   ========================================================================== */

import { initSmoothScroll } from './motion/scroll.js';
import { initRevealOnScroll } from './motion/reveal.js';
import { initMarquees } from './motion/marquee.js';
import { initCounters } from './motion/counters.js';
import { initNav } from './ui/nav.js';

const prefersReducedMotion =
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isSmallViewport =
  window.matchMedia('(max-width: 768px)').matches;

document.addEventListener('DOMContentLoaded', async () => {
  initNav();
  initMarquees();
  initCounters();
  initRevealOnScroll();

  // Belt-and-suspenders: if anything still hasn't been promoted after 2s
  // (e.g. IntersectionObserver missed an edge case on a slow first paint),
  // force-show everything currently above-or-near the fold.
  setTimeout(() => {
    document.querySelectorAll('.reveal:not(.is-in)').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 1.2) el.classList.add('is-in');
    });
  }, 2000);

  // Smooth scroll is opt-out per page via <html data-no-lenis>.
  // On mobile we skip Lenis entirely  native scroll is faster + more
  // predictable on iOS Safari, and the visual difference is minimal.
  if (
    !document.documentElement.hasAttribute('data-no-lenis')
    && !prefersReducedMotion
    && !isSmallViewport
  ) {
    initSmoothScroll();
  }

  // Hero canvas is expensive   lazy-load only when one is present
  // and the user isn't asking for reduced motion.
  const heroCanvas = document.querySelector('[data-hero-canvas]');
  if (heroCanvas && !prefersReducedMotion) {
    // On very small screens, use the CSS fallback (a static gradient + SVG blob).
    if (!isSmallViewport) {
      const { initHeroScene } = await import('./scenes/hero.js');
      initHeroScene(heroCanvas);
    }
  }
});
